import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NewSiteHeader from "../components/NewSiteHeader";
import LandingFooter from "../components/LandingFooter";
import { GUTTER } from "../components/NewSiteConstants";
import { pageTransitionTo } from "../components/PageTransition";
import PageBlackOverlay from "../components/PageBlackOverlay";

const MOBILE_BP = 1000;
const MOBILE_GUTTER = 20;

// Fluxo brand palette
const BRAND_A = "#8b5cf6"; // purple
const BRAND_B = "#3b82f6"; // blue
const BRAND_C = "#14b8a6"; // teal

const LEFT_KPIS = [
  { value: "300+", label: "PBR Textures", accent: BRAND_A },
  { value: "5",    label: "Integrated Studios", accent: BRAND_B },
  { value: "10+",  label: "Export Formats", accent: BRAND_C },
  { value: "0",    label: "Installs Required", accent: "#ec4899" },
];

const RIGHT_SLIDES = [
  {
    id: "pipeline",
    eyebrow: "",
    title: "Build. Cut. Ship.",
    subtitle: "From your first block to your final export, every stage of the Minecraft creation pipeline lives inside a single browser based studio.",
  },
  {
    id: "browser",
    eyebrow: "",
    title: "No installs. No plugins.",
    subtitle: "The full stack of creation tools runs natively in the browser. Open a tab, open a build, share your work in minutes.",
  },
  {
    id: "platform",
    eyebrow: "",
    title: "Every studio. One canvas.",
    subtitle: "Textures, blocks, worlds and connected sets. Every studio you'd normally install, running side by side in the browser.",
  },
];

/* ═══════ ANIMATED BRAND CLOUD — slow, subtle color swirl in brand palette ═══════ */
function BrandCloud() {
  // Blurred colour blobs drifting on independent orbits. `screen` blend
  // brightens them against the dark base; keep multiple overlapping so the
  // mix-blend-mode: difference text above reads as shifting off-whites.
  const blob = (bg, size, x, y, animName, dur) => ({
    position: "absolute",
    width: size, height: size,
    left: x, top: y,
    borderRadius: "50%",
    background: `radial-gradient(circle at 50% 50%, ${bg} 0%, ${bg}00 68%)`,
    filter: "blur(70px)",
    mixBlendMode: "screen",
    animation: `${animName} ${dur}s ease-in-out infinite`,
    willChange: "transform",
    pointerEvents: "none",
  });
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 1, pointerEvents: "none" }}>
      <div style={blob(BRAND_A,   "72%", "-12%", "-18%", "brandDriftA", 22)} />
      <div style={blob(BRAND_B,   "82%",  "18%",  "32%", "brandDriftB", 26)} />
      <div style={blob(BRAND_C,   "68%",  "48%", "-22%", "brandDriftC", 24)} />
      <div style={blob("#60a5fa", "62%",  "-6%",  "48%", "brandDriftE", 30)} />
      <div style={blob("#ec4899", "56%",  "38%",  "58%", "brandDriftD", 28)} />
    </div>
  );
}

/* ═══════ LIGHTNING TRAIL — canvas overlay pinned to right pane ═══════ */
const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
};
const mix = (c1, c2, t) => {
  const a = hexToRgb(c1); const b = hexToRgb(c2);
  return { r: Math.round(a.r + (b.r - a.r) * t), g: Math.round(a.g + (b.g - a.g) * t), b: Math.round(a.b + (b.b - a.b) * t) };
};

function LightningTrail({ containerRef }) {
  const canvasRef = useRef(null);
  const pointsRef = useRef([]);
  const rafRef = useRef(null);
  const lastMouseRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };
    resize();
    window.addEventListener("resize", resize);

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      lastMouseRef.current = { x, y };
      pointsRef.current.push({ x, y, age: 0 });
      if (pointsRef.current.length > 60) pointsRef.current.shift();
    };
    window.addEventListener("mousemove", onMove);

    const jagBetween = (p1, p2, segments, amplitude, seed) => {
      const pts = [p1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const nx = -dy, ny = dx;
      const len = Math.hypot(dx, dy) || 1;
      for (let i = 1; i < segments; i++) {
        const t = i / segments;
        const r = Math.sin(seed + i * 12.9898) * 43758.5453;
        const noise = (r - Math.floor(r)) - 0.5;
        const off = noise * amplitude;
        pts.push({ x: p1.x + dx * t + (nx / len) * off, y: p1.y + dy * t + (ny / len) * off });
      }
      pts.push(p2);
      return pts;
    };

    let tick = 0;
    const render = () => {
      const rect = container.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      for (const p of pointsRef.current) p.age += 1;
      pointsRef.current = pointsRef.current.filter(p => p.age < 90);
      if (pointsRef.current.length < 2) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }

      const drawPass = (widthMul, alphaMul, jitterAmp, blur, seedOffset) => {
        ctx.save();
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.shadowBlur = blur;
        ctx.shadowColor = BRAND_C;
        for (let i = 0; i < pointsRef.current.length - 1; i++) {
          const a = pointsRef.current[i];
          const b = pointsRef.current[i + 1];
          const alpha = Math.max(0, 1 - a.age / 80) * alphaMul;
          if (alpha <= 0) continue;
          const t = i / Math.max(1, pointsRef.current.length - 1);
          const col = t < 0.5 ? mix(BRAND_A, BRAND_B, t * 2) : mix(BRAND_B, BRAND_C, (t - 0.5) * 2);
          const segs = jagBetween(a, b, 4, jitterAmp, tick * 0.3 + i + seedOffset);
          ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${alpha})`;
          ctx.lineWidth = (2.2 - a.age * 0.02) * widthMul;
          ctx.beginPath();
          ctx.moveTo(segs[0].x, segs[0].y);
          for (let s = 1; s < segs.length; s++) ctx.lineTo(segs[s].x, segs[s].y);
          ctx.stroke();
        }
        ctx.restore();
      };

      drawPass(3.0, 0.35, 6, 22, 0);
      drawPass(1.6, 0.65, 4, 12, 100);
      drawPass(0.75, 1.0, 2, 4, 200);

      const head = lastMouseRef.current;
      if (head) {
        ctx.save();
        ctx.shadowBlur = 20;
        ctx.shadowColor = BRAND_C;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(head.x, head.y, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      tick += 1;
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="lightning-trail-canvas"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3 }}
    />
  );
}

export default function NewLandingPage() {
  const navigate = useNavigate();
  const [activeRight, setActiveRight] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BP);
  const scrollRef = useRef(null);
  const rightPaneRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { document.title = "LumenForge-MC · Build, Cut, Ship · Minecraft Creation Platform"; }, []);


  useEffect(() => {
    if (RIGHT_SLIDES.length <= 1) return;
    const delay = 8000;
    const t = setTimeout(() => setActiveRight(i => (i + 1) % RIGHT_SLIDES.length), delay);
    return () => clearTimeout(t);
  }, [activeRight]);

  const nextRight = useCallback(() => setActiveRight(i => (i + 1) % RIGHT_SLIDES.length), []);
  const gutter = isMobile ? MOBILE_GUTTER : GUTTER;

  /* ═══════ MOBILE LAYOUT — welcome section fills 100dvh, no top image. ═══════
     Per user request the landing page on mobile no longer includes the rotating
     top slideshow or the top-right F-logo. The welcome section (title + CTAs)
     takes the full viewport height so the F-mark sits centrally and everything
     is visible without scrolling. */
  if (isMobile) {
    return (
      <div data-testid="new-landing-page" style={{
        width: "100%", height: "100dvh", overflow: "hidden",
        background: "#070710", position: "relative",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <PageBlackOverlay />

        {/* Ambient brand colour cloud behind the welcome section. */}
        <div ref={rightPaneRef} style={{ position: "absolute", inset: 0, isolation: "isolate", overflow: "hidden" }}>
          <BrandCloud />
          <LightningTrail containerRef={rightPaneRef} />
        </div>

        <section data-testid="mobile-left-panel" style={{
          position: "relative", zIndex: 2,
          height: "100dvh",
          padding: `0 ${MOBILE_GUTTER}px`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          textAlign: "center",
        }}>
          <img
            src="/lumenforge-f-logo.webp"
            alt="LumenForge"
            style={{ width: 84, height: 84, objectFit: "contain", filter: "drop-shadow(0 6px 22px rgba(59,130,246,0.4))", marginBottom: 22 }}
          />
          <h2 style={{
            fontSize: "2.2rem", fontWeight: 800, color: "#fff",
            lineHeight: 1.08, letterSpacing: "-0.01em",
            textTransform: "uppercase",
            margin: "0 0 14px",
          }}>
            Welcome to{" "}
            <span style={{
              background: `linear-gradient(90deg, ${BRAND_A} 0%, ${BRAND_B} 55%, ${BRAND_C} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", color: "transparent",
              fontWeight: 800, letterSpacing: "-0.015em",
            }}>LumenForge-MC</span>.
          </h2>
          <p style={{
            fontSize: "0.68rem", fontWeight: 600, color: "rgba(255,255,255,0.55)",
            letterSpacing: "0.36em", textTransform: "uppercase",
            margin: "0 0 10px",
          }}>Build · Cut · Ship</p>
          <p style={{
            fontSize: "0.92rem", fontWeight: 400,
            color: "rgba(255,255,255,0.65)", lineHeight: 1.5,
            margin: "0 0 32px", maxWidth: 320,
          }}>The all-in-one Minecraft creation platform.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 340 }}>
            <button
              data-testid="mobile-cta-studio"
              onClick={() => pageTransitionTo("/studio", navigate)}
              style={{
                padding: "13px 20px", borderRadius: 4,
                background: BRAND_B, color: "#fff", border: `1px solid ${BRAND_B}`,
                fontSize: "0.78rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "inherit", cursor: "pointer",
                boxShadow: `0 8px 20px ${BRAND_B}44`,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              Start Building <span style={{ fontSize: "0.9rem", lineHeight: 1, fontWeight: 400 }}>→</span>
            </button>
            <button
              data-testid="mobile-cta-marketplace"
              onClick={() => navigate("/products/marketplace")}
              style={{
                padding: "12px 20px", borderRadius: 4,
                background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: "0.72rem", fontWeight: 600,
                letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "inherit", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              Visit the Store
            </button>
            <button
              data-testid="mobile-cta-download-builds"
              onClick={() => pageTransitionTo("/studio?app=world-builder&library=1", navigate)}
              style={{
                padding: "12px 20px", borderRadius: 4,
                background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: "0.72rem", fontWeight: 600,
                letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "inherit", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              Download Builds
            </button>
          </div>
        </section>

        {/* Floating burger */}
        <NewSiteHeader />

        <style>{`
          .hide-scrollbar-new::-webkit-scrollbar { display: none; }
          .hide-scrollbar-new { scrollbar-width: none; -ms-overflow-style: none; }
          @keyframes brandDriftA {
            0%,100% { transform: translate(0, 0) scale(1); }
            33%     { transform: translate(12%, 8%)  scale(1.08); }
            66%     { transform: translate(-6%, 14%) scale(0.94); }
          }
          @keyframes brandDriftB {
            0%,100% { transform: translate(0, 0) scale(1); }
            25%     { transform: translate(-14%, -10%) scale(1.06); }
            75%     { transform: translate(10%, 6%)   scale(0.92); }
          }
          @keyframes brandDriftC {
            0%,100% { transform: translate(0, 0) scale(1); }
            40%     { transform: translate(8%, -18%)  scale(1.10); }
            70%     { transform: translate(-12%, 8%)  scale(0.96); }
          }
          @keyframes brandDriftD {
            0%,100% { transform: translate(0, 0) scale(1); }
            50%     { transform: translate(-16%, -12%) scale(1.05); }
          }
          @keyframes brandDriftE {
            0%,100% { transform: translate(0, 0) scale(1); }
            30%     { transform: translate(14%, -8%)  scale(1.12); }
            65%     { transform: translate(-10%, 12%) scale(0.9); }
          }
        `}</style>

        {/* Slide-up frosted footer — mobile takes the full viewport */}
        <LandingFooter />
      </div>
    );
  }

  /* ═══════ DESKTOP LAYOUT — EVEN 50/50 SPLIT, NO HEADER, NO FOOTER ═══════ */
  return (
    <div data-testid="new-landing-page" style={{
      width: "100%", height: "100vh", overflow: "hidden",
      background: "#0a0a0a", position: "relative",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <PageBlackOverlay />

      <main style={{ position: "absolute", inset: 0 }}>
        {/* ─── LEFT PANE — colored swirl · slide copy · product image (clipped by middle line) ─── */}
        <div
          data-testid="left-panel"
          ref={rightPaneRef}
          style={{
            position: "absolute", top: 0, bottom: 0, left: 0, width: "50%",
            zIndex: 1,
            background: "#050510",
            overflow: "hidden",
            isolation: "isolate",
            borderRight: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <BrandCloud />
          <LightningTrail containerRef={rightPaneRef} />

          {/* Persistent product video — kept OUTSIDE the slide loop so the
              motion runs uninterrupted while the eyebrow/title/subtitle copy
              cycles behind it every 8s. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "8vh",
              bottom: "30%",
              right: "-8%",
              zIndex: 2,
              pointerEvents: "none",
              transform: "scaleX(0.9)",
              transformOrigin: "top right",
              filter: "drop-shadow(0 24px 60px rgba(6,10,30,0.55)) drop-shadow(0 0 40px rgba(59,130,246,0.22))",
              WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 55%, rgba(0,0,0,0.55) 82%, transparent 100%)",
              maskImage: "linear-gradient(to bottom, #000 0%, #000 55%, rgba(0,0,0,0.55) 82%, transparent 100%)",
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-label="LumenForge Studio preview"
              style={{
                height: "100%",
                width: "auto",
                display: "block",
                borderRadius: 14,
                objectFit: "cover",
              }}
            >
              {/* WebM first — smaller and covers Chromium-without-h264
                  (e.g. Playwright's default build). Chrome, Safari, Edge
                  and Firefox happily pick either. */}
              <source src="/landing_clip.webm" type="video/webm" />
              <source src="/landing_clip.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Slides — copy at bottom-LEFT of left pane. Only the text swaps;
              the video above keeps playing throughout. */}
          {RIGHT_SLIDES.map((card, i) => (
            <div
              key={card.id}
              data-testid={`right-card-${card.id}`}
              style={{
                position: "absolute", inset: 0,
                pointerEvents: "none",
                opacity: i === activeRight ? 1 : 0,
                transition: "opacity 1s ease",
              }}
            >
              <div style={{
                position: "absolute", left: gutter, bottom: 90,
                textAlign: "left", maxWidth: "80%",
                zIndex: 3,
              }}>
                {card.eyebrow && (
                  <div style={{
                    fontSize: "0.72rem", fontWeight: 600, color: "#fff",
                    letterSpacing: "0.36em", textTransform: "uppercase", marginBottom: 20,
                    opacity: i === activeRight ? 1 : 0,
                    transform: i === activeRight ? "translateY(0)" : "translateY(14px)",
                    transition: "all 0.8s ease 0.05s",
                  }}>{card.eyebrow}</div>
                )}
                <div style={{
                  fontSize: "2.6rem",
                  fontWeight: 600,
                  color: "#fff", lineHeight: 1.04,
                  letterSpacing: "0.005em", marginBottom: 18,
                  textTransform: "uppercase",
                  mixBlendMode: "difference",
                  opacity: i === activeRight ? 1 : 0,
                  transform: i === activeRight ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.8s ease 0.15s",
                }}>{card.title}</div>
                <div style={{
                  fontSize: "0.95rem", fontWeight: 400,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: 1.55, letterSpacing: "0.01em",
                  maxWidth: 480,
                  opacity: i === activeRight ? 1 : 0,
                  transform: i === activeRight ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.8s ease 0.3s",
                }}>{card.subtitle}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ─── RIGHT PANE — dark welcome, F-logo unchanged top-left position ─── */}
        <div data-testid="right-panel" style={{
          position: "absolute", top: 0, bottom: 0, left: "50%", right: 0,
          zIndex: 2,
          background: "#070710",
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          padding: `0 ${gutter}px`,
          textAlign: "center",
        }}>
          {/* F-logo — pinned to the same viewport position as before (top-left of the right pane). */}
          <div
            data-testid="right-brand"
            style={{
              position: "absolute", top: 32, left: gutter, zIndex: 12,
              display: "flex", alignItems: "center",
            }}
          >
            <img
              src="/lumenforge-f-logo.webp"
              alt="LumenForge"
              style={{ width: 72, height: 72, objectFit: "contain", filter: "drop-shadow(0 4px 24px rgba(59,130,246,0.35))" }}
            />
          </div>

          <h2 style={{
            fontSize: "2.6rem", fontWeight: 800, color: "#fff",
            lineHeight: 1.06, letterSpacing: "-0.01em",
            textTransform: "uppercase",
            margin: "0 0 18px",
          }}>
            Welcome to{" "}
            <span style={{
              background: `linear-gradient(90deg, ${BRAND_A} 0%, ${BRAND_B} 55%, ${BRAND_C} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", color: "transparent",
              fontWeight: 800,
              letterSpacing: "-0.015em",
            }}>LumenForge-MC</span>.
          </h2>

          <p style={{
            fontSize: "0.72rem", fontWeight: 600, color: "rgba(255,255,255,0.55)",
            letterSpacing: "0.36em", textTransform: "uppercase",
            margin: "0 0 12px",
          }}>Build · Cut · Ship</p>

          <p data-testid="left-tagline" style={{
            fontSize: "0.9rem", fontWeight: 400,
            color: "rgba(255,255,255,0.62)", lineHeight: 1.5,
            margin: "0 0 40px", maxWidth: 320,
          }}>The all-in-one Minecraft creation platform.</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: 360 }}>
            <button
              data-testid="left-cta-studio"
              onClick={() => pageTransitionTo("/studio", navigate)}
              style={{
                padding: "11px 20px", borderRadius: 4,
                background: BRAND_B, color: "#fff", border: `1px solid ${BRAND_B}`,
                fontSize: "0.76rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
                transition: "background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease",
                boxShadow: `0 8px 20px ${BRAND_B}44`,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#2563eb"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 26px ${BRAND_B}66`; }}
              onMouseLeave={e => { e.currentTarget.style.background = BRAND_B; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 8px 20px ${BRAND_B}44`; }}
            >
              Start Building <span style={{ fontSize: "0.9rem", lineHeight: 1, fontWeight: 400 }}>→</span>
            </button>
            <button
              data-testid="left-cta-marketplace"
              onClick={() => navigate("/products/marketplace")}
              style={{
                padding: "10px 20px", borderRadius: 4,
                background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                fontSize: "0.7rem", fontWeight: 600,
                letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.3s ease, border-color 0.3s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#fff"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.background = "transparent"; }}
            >
              Visit the Store
            </button>
            <button
              data-testid="left-cta-download-builds"
              onClick={() => pageTransitionTo("/studio?app=world-builder&library=1", navigate)}
              style={{
                padding: "10px 20px", borderRadius: 4,
                background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                fontSize: "0.7rem", fontWeight: 600,
                letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.3s ease, border-color 0.3s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#fff"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.background = "transparent"; }}
            >
              Download Builds
            </button>
          </div>

          <p data-testid="left-disclaimer" style={{
            marginTop: 22, maxWidth: 360,
            fontSize: "0.72rem", color: "rgba(255,255,255,0.48)", lineHeight: 1.55,
          }}>
            By continuing, you agree to our{" "}
            <a href="/terms"   onClick={(e) => { e.preventDefault(); navigate("/terms"); }}   style={{ color: "rgba(255,255,255,0.75)", textDecoration: "underline", textUnderlineOffset: 3 }}>Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate("/privacy"); }} style={{ color: "rgba(255,255,255,0.75)", textDecoration: "underline", textUnderlineOffset: 3 }}>Privacy Policy</a>.
          </p>
        </div>
      </main>

      {/* Veteran-owned badge removed on the landing per Feb 2026 direction —
          the top-right of the viewport now belongs to the ShopHeaderCluster
          (cart + profile). Badge still available if we want it back later. */}

      {/* Floating burger menu trigger (top-left) */}
      <NewSiteHeader triggerColor="#fff" />

      <style>{`
        .hide-scrollbar-new::-webkit-scrollbar { display: none; }
        .hide-scrollbar-new { scrollbar-width: none; -ms-overflow-style: none; }
        .scroll-pulse-up { animation: scrollPulseUp 2.5s ease-in-out infinite; }
        .scroll-pulse-down { animation: scrollPulseDown 2.5s ease-in-out infinite; }
        @keyframes scrollPulseUp { 0%,100% { height: 8px; opacity: 0.2; } 50% { height: 50px; opacity: 0.55; } }
        @keyframes scrollPulseDown { 0%,100% { height: 8px; opacity: 0.2; } 50% { height: 50px; opacity: 0.55; } }
        @keyframes brandDriftA {
          0%,100% { transform: translate(0, 0) scale(1); }
          33%     { transform: translate(12%, 8%)  scale(1.08); }
          66%     { transform: translate(-6%, 14%) scale(0.94); }
        }
        @keyframes brandDriftB {
          0%,100% { transform: translate(0, 0) scale(1); }
          25%     { transform: translate(-14%, -10%) scale(1.06); }
          75%     { transform: translate(10%, 6%)   scale(0.92); }
        }
        @keyframes brandDriftC {
          0%,100% { transform: translate(0, 0) scale(1); }
          40%     { transform: translate(8%, -18%)  scale(1.10); }
          70%     { transform: translate(-12%, 8%)  scale(0.96); }
        }
        @keyframes brandDriftD {
          0%,100% { transform: translate(0, 0) scale(1); }
          50%     { transform: translate(-16%, -12%) scale(1.05); }
        }
        @keyframes brandDriftE {
          0%,100% { transform: translate(0, 0) scale(1); }
          30%     { transform: translate(14%, -8%)  scale(1.12); }
          65%     { transform: translate(-10%, 12%) scale(0.9); }
        }
      `}</style>

      {/* Slide-up frosted footer with Explore / Support / Legal columns */}
      <LandingFooter />
    </div>
  );
}
